import { useState } from 'react';

import { useIsMutating, useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { match, P } from 'ts-pattern';

import { clientByIdOptions, clientProfileOptions, mutationKeys } from '@/entities/client';
import { useDiscardChangesGuard } from '@/shared/lib/use-discard-changes-guard';
import { useSheetStore } from '@/shared/stores/sheet-store';
import { SheetBreadcrumb } from '@/shared/ui/panels/sheet-breadcrumb';
import {
    SheetCloseButton,
    SheetDescription,
    SheetHeader,
    SheetPanel,
    SheetTitle,
} from '@/shared/ui/panels/sheet-panel';

import { EditClientProfileForm } from './edit-client-profile/edit-client-profile-form';

export function EditClientProfile() {
    const currentSheet = useSheetStore((state) => state.currentSheet);
    const closeAllSheets = useSheetStore((state) => state.closeAllSheets);

    const isOpen = currentSheet?.type === 'clients/edit-profile';
    const openClient = isOpen ? currentSheet.data : undefined;

    // The store drops the client the instant the sheet is left, but Radix keeps the content mounted
    // for the slide-out. Holding the last client keeps the body from flipping to the spinner while
    // it animates away; the queries stay disabled once closed, so nothing refetches meanwhile.
    const [retainedClient, setRetainedClient] = useState(openClient);
    if (openClient && openClient.id !== retainedClient?.id) {
        setRetainedClient(openClient);
    }
    const client = openClient ?? retainedClient;

    const profileQuery = useQuery({
        ...clientProfileOptions(client?.id ?? ''),
        enabled: isOpen && !!client?.id,
    });

    const { data: clientRecord } = useQuery({
        ...clientByIdOptions(client?.id ?? ''),
        enabled: isOpen && !!client?.id,
    });
    const name = clientRecord?.name ?? client?.name;

    const isSaving =
        useIsMutating({ mutationKey: mutationKeys.updateProfile(client?.id ?? '') }) > 0;

    const { confirmDiscardChanges, setDirty } = useDiscardChangesGuard(isSaving);

    const handleClose = () => confirmDiscardChanges(() => closeAllSheets());

    return (
        <SheetPanel
            type="clients/edit-profile"
            side="right"
            size="medium"
            variant="chart"
            showCloseButton={false}
            onRequestClose={handleClose}
        >
            <SheetHeader variant="breadcrumbs">
                <SheetBreadcrumb
                    getLabel={(sheet) =>
                        sheet.type === 'clients/details' && sheet.data.id === client?.id
                            ? name
                            : undefined
                    }
                    onNavigate={confirmDiscardChanges}
                />
                <SheetCloseButton onClick={handleClose} className="ml-auto shrink-0" />
            </SheetHeader>
            <SheetHeader variant="chart" bordered>
                <SheetTitle>Edit profile</SheetTitle>
                <SheetDescription className="sr-only">Edit profile for {name}.</SheetDescription>
            </SheetHeader>
            {match({ profile: profileQuery.data, client, isError: profileQuery.isError })
                .with(
                    { profile: P.nonNullable, client: P.nonNullable },
                    ({ profile, client: shown }) => (
                        <EditClientProfileForm
                            key={shown.id}
                            profile={profile}
                            onDirtyChange={setDirty}
                        />
                    ),
                )
                .with({ isError: true }, () => (
                    <div className="flex flex-1 items-center justify-center px-[var(--sheet-gutter)]">
                        <p className="text-sm text-muted-foreground">
                            Unable to load this client's profile. Try again later.
                        </p>
                    </div>
                ))
                .otherwise(() => (
                    <div className="flex flex-1 items-center justify-center">
                        <Loader2 className="h-8 w-8 animate-spin" />
                    </div>
                ))}
        </SheetPanel>
    );
}
